import React from 'react';
import { isEmpty } from 'lodash';

import { useStyles } from '~/assets/styles';
import { Text } from '~/components/core';
import IconButton from '~/components/core/Atomic/Buttons/IconButton';
import Grid from '~/components/core/Atomic/Grid/Grid';
import InputAdornment from '~/components/core/Atomic/InputAdornment';
import Link from '~/components/core/Atomic/Link/Link';
import MenuItem from '~/components/core/Atomic/MenuItem';
import InnerCard from '~/components/core/Cards/InnerCard';
import TextField from '~/components/core/Molecules/Fields/TextField';
import { useLobConfiguration } from '~/components/hooks/useLobConfiguration';
import CancelIcon from '~/components/icons/CancelIcon';
import CloseIcon_Deprecated from '~/components/icons/CloseIcon_Deprecated';
import { useSysconfig } from '~/components/SystemConfiguration/SystemConfigurationScreen';
import { subOrgIdToNameDict } from '~/Utils';
import cn from '~/Utils/cn';
import { getLobDescription } from '~/Utils/lobUtils';

import { FILTERS_DEFAULT } from './constants';
import type { Filters, SetFilters } from './types';

type ConfigurationFiltersProps = {
  filters: Filters;
  setFilters: SetFilters;
  searchPlaceholder: string;
  headerSlot?: React.ReactNode;
};

const ConfigurationFilters = ({
  filters,
  setFilters,
  searchPlaceholder,
  headerSlot,
}: ConfigurationFiltersProps): JSX.Element => {
  const { searchText, selectedLob, subOrganizationIds } = filters;
  const { classes } = useStyles();
  const { lobConfigurationsDict } = useLobConfiguration();
  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-ignore
  const { organization, organizationOperationalDetails } = useSysconfig();
  const subOrgIdToName = subOrgIdToNameDict(organization);

  const resetFilters = () => {
    setFilters({ ...FILTERS_DEFAULT });
  };

  return (
    <Grid container direction="column" spacing={2}>
      <Grid item xs={2} className="my-4">
        {/* Capped on a wrapper: a column Grid resets its items' max-width with a selector that outranks a utility. */}
        <div className="max-w-[400px]">
          <TextField
            fullWidth
            className={classes.textField}
            value={searchText}
            onChange={(value) => setFilters({ ...filters, searchText: value })}
            InputProps={{
              placeholder: searchPlaceholder,
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    className={cn(`p-2 ${isEmpty(searchText) ? 'hidden' : 'visible'} mr-4`)}
                    title="Clear"
                    onClick={() => setFilters({ ...filters, searchText: '' })}
                  >
                    <CloseIcon_Deprecated size={10} />
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
        </div>
      </Grid>
      {headerSlot && <Grid item>{headerSlot}</Grid>}
      <Grid item xs={12}>
        <InnerCard className="p-8">
          <Grid container direction="column">
            <Grid item xs={12} className="mb-4">
              <Text variant={Text.VARIANTS.LG} weight={Text.WEIGHTS.SEMI_BOLD}>
                Filters
              </Text>
            </Grid>
          </Grid>
          <Grid container spacing={2} className="flex items-center pb-12">
            {organization.sub_organizations_enabled && (
              <Grid item>
                <TextField
                  className={classes.textFieldRow}
                  label="By Sub-Organization"
                  onChange={(value) => setFilters({ ...filters, subOrganizationIds: [Number(value)] })}
                  select
                  value={subOrganizationIds}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          className={cn(`p-2 ${isEmpty(subOrganizationIds) ? 'hidden' : 'visible'} mr-20`)}
                          title="Clear"
                          onClick={() => setFilters({ ...filters, subOrganizationIds: [] })}
                        >
                          <CloseIcon_Deprecated size={10} />
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                >
                  {organizationOperationalDetails.subOrganizations.map(({ id }: { id: number }) => (
                    <MenuItem key={id} value={id}>
                      {subOrgIdToName[id]}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
            )}
            <Grid item>
              <TextField
                className={classes.textFieldRow}
                label="By Line of Business"
                onChange={(value) => setFilters({ ...filters, selectedLob: value })}
                select
                value={selectedLob}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        className={cn(`p-2 ${isEmpty(selectedLob) ? 'hidden' : 'visible'} mr-20`)}
                        title="Clear"
                        onClick={() => setFilters({ ...filters, selectedLob: '' })}
                      >
                        <CloseIcon_Deprecated size={10} />
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              >
                {organizationOperationalDetails.orgLevelSupportedLobs.map((lob: string) => (
                  <MenuItem key={lob} value={lob}>
                    {getLobDescription(lob, lobConfigurationsDict)}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>

            <Grid item>
              <Link
                href=""
                onClick={(e) => {
                  e.preventDefault();
                  resetFilters();
                }}
                className={cn(classes.alignedContainerForGrid, classes.secondaryLink, 'pt-1.5')}
              >
                <CancelIcon className={cn(classes.filledIcon, classes.leftButtonIcon)} />
                Clear all filters
              </Link>
            </Grid>
          </Grid>
        </InnerCard>
      </Grid>
    </Grid>
  );
};

export default ConfigurationFilters;
